import { BaseTariffPage } from "@/components/tarif/BaseTariffPage";
import { Shield, CheckCircle2, Clock, Phone, FileText, Stethoscope, Users, Building2, Lock, Globe, Zap, HeartPulse, Briefcase, Database } from "lucide-react";

const GOTHAER_COLOR = "#005AA0";

const GothaerTarif = () => {
  const heroFeatures = [
    { icon: Phone, title: "24h-Cyber-Soforthilfe", subtitle: "Notfallhotline" },
    { icon: Shield, title: "Umfassender Schutz", subtitle: "Alle Cyber-Gefahren" },
    { icon: Globe, title: "IT-Prävention", subtitle: "Awareness-Trainings" }
  ];

  const coverageAreas = [
    {
      icon: Zap,
      title: "Cyber-Sofortmaßnahmen",
      items: [
        "24-Stunden-Cyber-Soforthilfe-Hotline",
        "IT-Forensik und Schadensanalyse",
        "Koordination von IT-Experten",
        "Krisenmanagement und PR-Beratung",
        "Systemwiederherstellung"
      ]
    },
    {
      icon: Clock,
      title: "Betriebsunterbrechung",
      items: [
        "Deckung von Ertragsausfällen",
        "Fortlaufende Fixkosten",
        "Mehrkosten für Notfallsysteme",
        "Kosten für temporäre Lösungen",
        "Betriebsunterbrechung ohne Wartezeit"
      ]
    },
    {
      icon: Database,
      title: "Daten & IT-Systeme",
      items: [
        "Datenwiederherstellung",
        "IT-forensische Untersuchungen",
        "Systemrekonstruktion",
        "Backup-Wiederherstellung",
        "Wiederaufbau der IT-Infrastruktur"
      ]
    },
    {
      icon: Lock,
      title: "Cyber-Haftpflicht",
      items: [
        "DSGVO-Bußgelder und Sanktionen",
        "Schadensersatzansprüche Dritter",
        "Rechtsanwalts- und Gerichtskosten",
        "Cyber-Erpressung inkl. Lösegelder",
        "Benachrichtigungskosten bei Datenpannen"
      ]
    }
  ];

  const targetGroups = [
    {
      icon: Stethoscope,
      title: "Arztpraxen aller Fachrichtungen",
      description: "Hausärzte, Fachärzte, Einzelpraxen und Gemeinschaftspraxen mit digitaler Patientenverwaltung",
      benefits: [
        "24h-Cyber-Soforthilfe-Hotline",
        "Umfassender Datenschutz",
        "DSGVO-konforme Absicherung",
        "Betriebsunterbrechungsschutz"
      ],
      recommendation: "Empfohlene Deckung: 100.000 - 300.000 €"
    },
    {
      icon: HeartPulse,
      title: "MVZ & Gesundheitszentren",
      description: "Medizinische Versorgungszentren mit vernetzten IT-Systemen und mehreren Standorten",
      benefits: [
        "Multi-Standort-Schutz",
        "Schutz komplexer IT-Infrastrukturen",
        "Umfassende IT-Forensik",
        "Flexible Deckungskonzepte"
      ],
      recommendation: "Empfohlene Deckung: 300.000 - 1.000.000 €"
    },
    {
      icon: Building2,
      title: "Kliniken & Krankenhäuser",
      description: "Große medizinische Einrichtungen mit kritischer digitaler Infrastruktur",
      benefits: [
        "Schutz medizinischer Geräte",
        "Notfallmanagement kritischer Systeme",
        "Höchste Deckungssummen",
        "Individuelle Lösungen"
      ],
      recommendation: "Empfohlene Deckung: 1.000.000 - 5.000.000 €"
    },
    {
      icon: Briefcase,
      title: "Therapeutische Praxen",
      description: "Physiotherapie, Ergotherapie, Logopädie und psychotherapeutische Praxen",
      benefits: [
        "Günstige Tarife für kleine Praxen",
        "Schutz digitaler Therapiedaten",
        "Mitarbeiterschulungen",
        "Schnelle Schadenregulierung"
      ],
      recommendation: "Empfohlene Deckung: 50.000 - 150.000 €"
    }
  ];

  const insuranceConditions = [
    {
      icon: Shield,
      title: "Versicherungssummen",
      description: "Flexibel von 50.000 EUR bis 5 Mio. EUR wählbar - individuell anpassbar an Ihre Praxisgröße"
    },
    {
      icon: FileText,
      title: "Selbstbeteiligung",
      description: "Wahlweise 0 EUR, 500 EUR oder 1.000 EUR SB - für optimale Beitragsgestaltung"
    },
    {
      icon: CheckCircle2,
      title: "Sofortschutz",
      description: "Keine Wartezeiten - voller Versicherungsschutz ab Tag 1"
    },
    {
      icon: Clock,
      title: "Vertragslaufzeit",
      description: "1 Jahr mit automatischer Verlängerung, 3 Monate Kündigungsfrist zum Vertragsende"
    },
    {
      icon: Globe,
      title: "Präventionsleistungen",
      description: "Cyber-Awareness-Trainings, Phishing-Simulationen und IT-Sicherheitsberatung inklusive"
    },
    {
      icon: Users,
      title: "Umfassender Schutz",
      description: "Gilt für alle Mitarbeiter, Geräte und beauftragte IT-Dienstleister"
    }
  ];

  const faqs = [
    {
      question: "Was leistet die Gothaer Cyberversicherung?",
      answer: "Die Gothaer Cyberversicherung bietet umfassenden Schutz bei Cyber-Vorfällen: 24h-Soforthilfe-Hotline, IT-Forensik, Datenwiederherstellung, Betriebsunterbrechungsschutz, Cyber-Erpressungsschutz, DSGVO-Bußgelder und Cyber-Haftpflicht. Zusätzlich sind präventive Schulungen und IT-Sicherheitsberatung enthalten."
    },
    {
      question: "Für wen ist die Gothaer Cyberversicherung geeignet?",
      answer: "Die Versicherung eignet sich für alle medizinischen Einrichtungen: Arztpraxen, MVZ, Kliniken, Therapiepraxen und Heilberufe, die digitale Patientendaten verarbeiten. Durch flexible Deckungssummen ist sie von kleinen Praxen bis großen Kliniken skalierbar."
    },
    {
      question: "Wie hoch sollte die Versicherungssumme sein?",
      answer: "Die optimale Versicherungssumme richtet sich nach Praxisgröße und Risiko: Kleine Praxen 50.000-150.000 EUR, mittlere Praxen 150.000-300.000 EUR, MVZ 300.000-1.000.000 EUR, Kliniken 1-5 Mio. EUR."
    },
    {
      question: "Was kostet die Gothaer Cyberversicherung?",
      answer: "Der Beitrag hängt von verschiedenen Faktoren ab: Fachrichtung, Praxisgröße, Jahresumsatz, IT-Sicherheitsmaßnahmen und gewählte Versicherungssumme. Für ein individuelles Angebot kontaktieren Sie uns für eine kostenlose Beratung."
    },
    {
      question: "Gibt es Wartezeiten?",
      answer: "Nein, es gibt keine Wartezeiten. Der vollständige Versicherungsschutz und die 24h-Cyber-Soforthilfe-Hotline sind ab dem ersten Tag verfügbar."
    },
    {
      question: "Sind DSGVO-Bußgelder versichert?",
      answer: "Ja, die Gothaer Cyberversicherung deckt DSGVO-Bußgelder ab, soweit rechtlich versicherbar. Ebenso enthalten sind Kosten für Datenschutzbeauftragte, Behördenmeldungen und Betroffenenbenachrichtigungen."
    },
    {
      question: "Wie funktioniert die 24h-Cyber-Soforthilfe-Hotline?",
      answer: "Bei einem Cyber-Vorfall steht die Hotline rund um die Uhr zur Verfügung. Sie erhalten sofortigen Zugang zu IT-Spezialisten, die den Angriff stoppen, Schäden analysieren und die Wiederherstellung koordinieren."
    },
    {
      question: "Ist Cyber-Erpressung versichert?",
      answer: "Ja, die Versicherung deckt Cyber-Erpressungen ab: Lösegeldzahlungen (soweit rechtlich zulässig), Verhandlungskosten und Kosten für spezialisierte Cyber-Sicherheitsexperten."
    },
    {
      question: "Welche Präventionsleistungen sind enthalten?",
      answer: "Gothaer bietet Cyber-Awareness-Schulungen für Mitarbeiter, regelmäßige Phishing-Simulationen, IT-Sicherheitsberatung und Online-Trainings zur Sensibilisierung für Cyber-Risiken."
    },
    {
      question: "Was passiert bei einem Datenleck?",
      answer: "Die Versicherung übernimmt alle Kosten: Benachrichtigung betroffener Personen, Meldung an Datenschutzbehörden, PR-Krisenmanagement, Rechtsberatung, Schadensersatzforderungen und eventuelle DSGVO-Bußgelder."
    }
  ];

  const documents = [
    {
      title: "Produktinformationsblatt Cyberversicherung",
      type: "PDF",
      url: "https://www.gothaer.de/download/cyberversicherung-pib.pdf"
    },
    {
      title: "Versicherungsbedingungen (AVB)",
      type: "PDF",
      url: "https://www.gothaer.de/download/cyberversicherung-avb.pdf"
    }
  ];

  return (
    <BaseTariffPage
      companyName="Gothaer"
      companyColor={GOTHAER_COLOR}
      productRating={{ value: 4.6, count: 329 }}
      priceRange="58-205"
      pageTitle="Gothaer Cyberversicherung für Arztpraxen - Umfassender Cyber-Schutz"
      metaDescription="Die Gothaer Cyberversicherung bietet Arztpraxen, MVZ und Kliniken umfassenden Schutz vor Cyber-Risiken mit 24h-Soforthilfe-Hotline, DSGVO-Schutz und IT-Präventionsleistungen."
      metaKeywords="Gothaer Cyberversicherung, Cyber-Versicherung Arztpraxis, Gothaer Cyber-Schutz, Arztpraxis Cybersicherheit"
      canonicalUrl="https://praxis-versichert.de/tarifwissen/gothaer"
      heroLabel="Traditionsversicherer mit moderner Cyber-Kompetenz"
      heroTitle={<>Gothaer Cyberversicherung<br />für medizinische Einrichtungen</>}
      heroDescription="Umfassender Cyber-Schutz für Arztpraxen, MVZ und Kliniken mit 24-Stunden-Cyber-Soforthilfe-Hotline, vollständiger Betriebsunterbrechungsdeckung und präventiven IT-Sicherheitsleistungen. Von einem der traditionsreichsten deutschen Versicherer."
      heroFeatures={heroFeatures}
      alertTitle="Warum Gothaer Cyberversicherung?"
      alertDescription="Cyber-Angriffe auf Gesundheitseinrichtungen nehmen dramatisch zu - 2023 war jede dritte Arztpraxis betroffen. Die Gothaer Cyberversicherung schützt Sie mit sofortiger 24h-Hilfe, umfassenden Leistungen und über 200 Jahren Versicherungserfahrung."
      coverageAreas={coverageAreas}
      targetGroups={targetGroups}
      insuranceConditions={insuranceConditions}
      faqs={faqs}
      documents={documents}
      caseStudies={[
        {
          title: "Ransomware-Angriff auf Orthopädie-Praxis",
          description: "Emotet verschlüsselte Röntgenbilder. 8 Tage geschlossen.",
          outcome: "Gothaer koordinierte IT-Forensik. Versicherung: 53.600 €.",
          damage: "53.600 €"
        }
      ]}
    />
  );
};

export default GothaerTarif;
